import React, { useEffect, useState } from 'react';
import { SaveIcon, XIcon, MailIcon, SendIcon, AlertTriangleIcon, InfoIcon } from 'lucide-react';
import { EmailTemplate, MessageModule, Role } from './MessagesManagement';
interface EmailTemplateFormProps {
  template: EmailTemplate | null;
  availableRoles: Role[];
  currentUserRoles: string[];
  onSave: (template: EmailTemplate) => void;
  onCancel: () => void;
  onSendTest: (id: string, email: string) => void;
  locale: string;
}
export function EmailTemplateForm({
  template,
  availableRoles,
  currentUserRoles,
  onSave,
  onCancel,
  onSendTest,
  locale
}: EmailTemplateFormProps) {
  const isAdmin = currentUserRoles.includes('Administrator');
  const [formData, setFormData] = useState<Omit<EmailTemplate, 'id' | 'createdAt' | 'createdBy' | 'usageCount'>>({
    key: '',
    subject: '',
    bodyHtml: '',
    mergeFields: [],
    module: 'PO',
    languageCode: 'en-AU',
    isActive: true,
    isSystemLocked: false,
    editableByRoles: [],
    modifiedAt: undefined,
    modifiedBy: undefined,
    lastUsedAt: undefined
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [selectedRoles, setSelectedRoles] = useState<string[]>([]);
  const [showPreview, setShowPreview] = useState(false);
  const [mergeFieldInput, setMergeFieldInput] = useState('');
  const [testEmailAddress, setTestEmailAddress] = useState('');
  // Common merge fields for different modules
  const commonMergeFields = {
    PO: ['PONumber', 'VendorName', 'CompanyName', 'PODate', 'POAmount', 'ContactEmail'],
    VM: ['VendorName', 'CompanyName', 'VendorID', 'PortalURL', 'Username', 'TempPassword'],
    AP: ['ApproverName', 'InvoiceNumber', 'VendorName', 'InvoiceAmount', 'DueDate', 'ApprovalLink', 'CompanyName'],
    OCR: ['RecipientName', 'DocumentID', 'ExceptionType', 'DateReceived', 'ReviewLink', 'CompanyName']
  };
  // Initialize form data when editing an existing template
  useEffect(() => {
    if (template) {
      setFormData({
        key: template.key,
        subject: template.subject,
        bodyHtml: template.bodyHtml,
        mergeFields: template.mergeFields,
        module: template.module,
        languageCode: template.languageCode,
        isActive: template.isActive,
        isSystemLocked: template.isSystemLocked,
        editableByRoles: template.editableByRoles,
        modifiedAt: template.modifiedAt,
        modifiedBy: template.modifiedBy,
        lastUsedAt: template.lastUsedAt
      });
      setSelectedRoles(template.editableByRoles);
    }
  }, [template]);
  // Determine if current user can edit email templates
  const canEditEmailTemplates = (): boolean => {
    if (isAdmin) return true;
    const userRole = availableRoles.find(role => currentUserRoles.includes(role.name));
    if (!userRole) return false;
    return userRole.canEditEmailTemplates;
  };
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const {
      name,
      value
    } = e.target;
    // Special handling for module to update merge fields
    if (name === 'module') {
      const moduleValue = value as MessageModule;
      if (moduleValue in commonMergeFields) {
        setFormData({
          ...formData,
          [name]: value,
          mergeFields: commonMergeFields[moduleValue as keyof typeof commonMergeFields]
        });
      } else {
        setFormData({
          ...formData,
          [name]: value
        });
      }
    } else {
      setFormData({
        ...formData,
        [name]: value
      });
    }
    // Clear error for this field if it exists
    if (errors[name]) {
      const newErrors = {
        ...errors
      };
      delete newErrors[name];
      setErrors(newErrors);
    }
  };
  const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const {
      name,
      checked
    } = e.target;
    setFormData({
      ...formData,
      [name]: checked
    });
  };
  const handleRoleToggle = (role: string) => {
    const newSelectedRoles = selectedRoles.includes(role) ? selectedRoles.filter(r => r !== role) : [...selectedRoles, role];
    setSelectedRoles(newSelectedRoles);
    setFormData({
      ...formData,
      editableByRoles: newSelectedRoles
    });
  };
  const handleAddMergeField = () => {
    if (mergeFieldInput && !formData.mergeFields.includes(mergeFieldInput)) {
      setFormData({
        ...formData,
        mergeFields: [...formData.mergeFields, mergeFieldInput]
      });
      setMergeFieldInput('');
    }
  };
  const handleRemoveMergeField = (field: string) => {
    setFormData({
      ...formData,
      mergeFields: formData.mergeFields.filter(f => f !== field)
    });
  };
  const handleSendTest = () => {
    if (template && testEmailAddress && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(testEmailAddress)) {
      onSendTest(template.id, testEmailAddress);
    } else {
      alert('Please enter a valid email address');
    }
  };
  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};
    if (!formData.key) {
      newErrors.key = 'Key is required';
    } else if (!/^[A-Z0-9_]+$/.test(formData.key)) {
      newErrors.key = 'Key must contain only uppercase letters, numbers, and underscores';
    }
    if (!formData.subject) {
      newErrors.subject = 'Subject is required';
    }
    if (!formData.bodyHtml) {
      newErrors.bodyHtml = 'Email body is required';
    }
    if (!canEditEmailTemplates()) {
      newErrors.general = "You don't have permission to edit email templates";
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (validateForm()) {
      onSave({
        ...(template ? {
          id: template.id
        } : {
          id: ''
        }),
        ...formData,
        createdAt: template?.createdAt || new Date().toISOString(),
        createdBy: template?.createdBy || 'Current User',
        usageCount: template?.usageCount || 0
      });
    }
  };
  return <div className="bg-white shadow overflow-hidden sm:rounded-lg" data-id="element-16170">
      <div className="px-4 py-5 sm:px-6 flex justify-between items-center" data-id="element-16171">
        <div data-id="element-16172">
          <h3 className="text-lg leading-6 font-medium text-gray-900" data-id="element-16173">
            {template ? 'Edit Email Template' : 'Add New Email Template'}
          </h3>
          <p className="mt-1 max-w-2xl text-sm text-gray-500" data-id="element-16174">
            {template ? 'Update an existing email template' : 'Create a new email template for system communications'}
          </p>
        </div>
        <div className="flex space-x-3" data-id="element-16175">
          <button type="button" onClick={() => setShowPreview(!showPreview)} className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-16176">
            <InfoIcon className="h-4 w-4 mr-2" data-id="element-16177" />
            {showPreview ? 'Hide Preview' : 'Show Preview'}
          </button>
          {template && <div className="flex items-center space-x-2" data-id="element-16178">
              <input type="email" value={testEmailAddress} onChange={e => setTestEmailAddress(e.target.value)} placeholder="Test email address" className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-48 sm:text-sm border-gray-300 rounded-md" data-id="element-16179" />
              <button type="button" onClick={handleSendTest} className="inline-flex items-center px-3 py-2 border border-transparent shadow-sm text-sm leading-4 font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-16180">
                <SendIcon className="h-4 w-4 mr-2" data-id="element-16181" />
                Send Test
              </button>
            </div>}
        </div>
      </div>
      <div className="border-t border-gray-200 px-4 py-5 sm:px-6" data-id="element-16182">
        {errors.general && <div className="rounded-md bg-red-50 p-4 mb-6" data-id="element-16183">
            <div className="flex" data-id="element-16184">
              <div className="flex-shrink-0" data-id="element-16185">
                <AlertTriangleIcon className="h-5 w-5 text-red-400" data-id="element-16186" />
              </div>
              <div className="ml-3" data-id="element-16187">
                <h3 className="text-sm font-medium text-red-800" data-id="element-16188">Error</h3>
                <div className="mt-2 text-sm text-red-700" data-id="element-16189">
                  <p data-id="element-16190">{errors.general}</p>
                </div>
              </div>
            </div>
          </div>}
        <form onSubmit={handleSubmit} className="space-y-6" data-id="element-16191">
          <div className="grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6" data-id="element-16192">
            {/* Module */}
            <div className="sm:col-span-3" data-id="element-16193">
              <label htmlFor="module" className="block text-sm font-medium text-gray-700" data-id="element-16194">
                Module <span className="text-red-500" data-id="element-16195">*</span>
              </label>
              <div className="mt-1" data-id="element-16196">
                <select id="module" name="module" value={formData.module} onChange={handleChange} disabled={template?.isSystemLocked} className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md" data-id="element-16197">
                  {(['AP', 'PO', 'VM', 'OCR', 'DI', 'Financial', 'Settings', 'Core'] as MessageModule[]).map(module => <option key={module} value={module} data-id="element-16198">
                      {module}
                    </option>)}
                </select>
                <p className="mt-2 text-xs text-gray-500" data-id="element-16199">
                  Selecting a module will suggest common merge fields for that
                  area
                </p>
              </div>
            </div>
            {/* Template Key */}
            <div className="sm:col-span-3" data-id="element-16200">
              <label htmlFor="key" className="block text-sm font-medium text-gray-700" data-id="element-16201">
                Template Key <span className="text-red-500" data-id="element-16202">*</span>
              </label>
              <div className="mt-1" data-id="element-16203">
                <input type="text" name="key" id="key" value={formData.key} onChange={handleChange} disabled={template?.isSystemLocked} placeholder="e.g., PO_EMAIL_VENDOR_NEW_PO" className={`shadow-sm block w-full sm:text-sm rounded-md ${errors.key ? 'border-red-300 text-red-900 placeholder-red-300 focus:outline-none focus:ring-red-500 focus:border-red-500' : 'border-gray-300 focus:ring-indigo-500 focus:border-indigo-500'}`} data-id="element-16204" />
                {errors.key ? <p className="mt-2 text-sm text-red-600" data-id="element-16205">{errors.key}</p> : <p className="mt-2 text-xs text-gray-500" data-id="element-16206">
                    Use uppercase letters, numbers, and underscores. Prefix with
                    module name.
                  </p>}
              </div>
            </div>
            {/* Subject */}
            <div className="sm:col-span-6" data-id="element-16207">
              <label htmlFor="subject" className="block text-sm font-medium text-gray-700" data-id="element-16208">
                Email Subject <span className="text-red-500" data-id="element-16209">*</span>
              </label>
              <div className="mt-1" data-id="element-16210">
                <input type="text" name="subject" id="subject" value={formData.subject} onChange={handleChange} disabled={template?.isSystemLocked} placeholder="e.g., New Purchase Order {{PONumber}} from {{CompanyName}}" className={`shadow-sm block w-full sm:text-sm rounded-md ${errors.subject ? 'border-red-300 text-red-900 placeholder-red-300 focus:outline-none focus:ring-red-500 focus:border-red-500' : 'border-gray-300 focus:ring-indigo-500 focus:border-indigo-500'}`} data-id="element-16211" />
                {errors.subject ? <p className="mt-2 text-sm text-red-600" data-id="element-16212">{errors.subject}</p> : <p className="mt-2 text-xs text-gray-500" data-id="element-16213">
                    You can use merge fields like {'{{FieldName}}'} in the
                    subject
                  </p>}
              </div>
            </div>
            {/* Merge Fields */}
            <div className="sm:col-span-6" data-id="element-16214">
              <label htmlFor="mergeFields" className="block text-sm font-medium text-gray-700" data-id="element-16215">
                Merge Fields
              </label>
              <div className="mt-1" data-id="element-16216">
                <div className="flex space-x-2" data-id="element-16217">
                  <input type="text" value={mergeFieldInput} onChange={e => setMergeFieldInput(e.target.value)} placeholder="Add merge field name" className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md" data-id="element-16218" />
                  <button type="button" onClick={handleAddMergeField} className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-16219">
                    Add
                  </button>
                </div>
                <div className="mt-2 flex flex-wrap gap-2" data-id="element-16220">
                  {formData.mergeFields.map(field => <span key={field} className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800" data-id="element-16221">
                      {`{{${field}}}`}
                      <button type="button" onClick={() => handleRemoveMergeField(field)} className="ml-1.5 inline-flex items-center justify-center h-4 w-4 rounded-full text-blue-400 hover:text-blue-600 focus:outline-none focus:text-blue-600" data-id="element-16222">
                        <XIcon className="h-3 w-3" data-id="element-16223" />
                      </button>
                    </span>)}
                </div>
                <p className="mt-2 text-xs text-gray-500" data-id="element-16224">
                  These fields will be replaced with actual values when the
                  email is sent
                </p>
              </div>
            </div>
            {/* Email Body */}
            <div className="sm:col-span-6" data-id="element-16225">
              <label htmlFor="bodyHtml" className="block text-sm font-medium text-gray-700" data-id="element-16226">
                Email Body (HTML) <span className="text-red-500" data-id="element-16227">*</span>
              </label>
              <div className="mt-1" data-id="element-16228">
                <textarea id="bodyHtml" name="bodyHtml" rows={12} value={formData.bodyHtml} onChange={handleChange} disabled={template?.isSystemLocked} className={`shadow-sm block w-full sm:text-sm rounded-md font-mono ${errors.bodyHtml ? 'border-red-300 text-red-900 placeholder-red-300 focus:outline-none focus:ring-red-500 focus:border-red-500' : 'border-gray-300 focus:ring-indigo-500 focus:border-indigo-500'}`} data-id="element-16229" />
                {errors.bodyHtml && <p className="mt-2 text-sm text-red-600" data-id="element-16230">{errors.bodyHtml}</p>}
                <p className="mt-2 text-xs text-gray-500" data-id="element-16231">
                  Use HTML to format your email. You can use merge fields like{' '}
                  {'{{FieldName}}'} in the body.
                </p>
              </div>
            </div>
            {/* Status & System Lock */}
            <div className="sm:col-span-6" data-id="element-16232">
              <div className="flex items-start" data-id="element-16233">
                <div className="flex items-center h-5" data-id="element-16234">
                  <input id="isActive" name="isActive" type="checkbox" checked={formData.isActive} onChange={handleCheckboxChange} disabled={template?.isSystemLocked} className="focus:ring-indigo-500 h-4 w-4 text-indigo-600 border-gray-300 rounded" data-id="element-16235" />
                </div>
                <div className="ml-3 text-sm" data-id="element-16236">
                  <label htmlFor="isActive" className="font-medium text-gray-700" data-id="element-16237">
                    Active
                  </label>
                  <p className="text-gray-500" data-id="element-16238">
                    Template will be used when active
                  </p>
                </div>
              </div>
              {isAdmin && !template?.isSystemLocked && <div className="flex items-start mt-4" data-id="element-16239">
                  <div className="flex items-center h-5" data-id="element-16240">
                    <input id="isSystemLocked" name="isSystemLocked" type="checkbox" checked={formData.isSystemLocked} onChange={handleCheckboxChange} className="focus:ring-indigo-500 h-4 w-4 text-indigo-600 border-gray-300 rounded" data-id="element-16241" />
                  </div>
                  <div className="ml-3 text-sm" data-id="element-16242">
                    <label htmlFor="isSystemLocked" className="font-medium text-gray-700" data-id="element-16243">
                      System Locked
                    </label>
                    <p className="text-gray-500" data-id="element-16244">
                      Prevent this template from being edited
                    </p>
                  </div>
                </div>}
            </div>
            {/* Role-Based Access Control */}
            <div className="sm:col-span-6" data-id="element-16245">
              <fieldset data-id="element-16246">
                <legend className="text-sm font-medium text-gray-700" data-id="element-16247">
                  Editable By Roles
                </legend>
                <div className="mt-2 space-y-4" data-id="element-16248">
                  <p className="text-sm text-gray-500" data-id="element-16249">
                    Select which roles can edit this template. If none are
                    selected, only administrators can edit.
                  </p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4" data-id="element-16250">
                    {availableRoles.map(role => <div key={role.id} className="flex items-start" data-id="element-16251">
                        <div className="flex items-center h-5" data-id="element-16252">
                          <input id={`role-${role.id}`} type="checkbox" checked={selectedRoles.includes(role.name)} onChange={() => handleRoleToggle(role.name)} disabled={template?.isSystemLocked || !canEditEmailTemplates()} className="focus:ring-indigo-500 h-4 w-4 text-indigo-600 border-gray-300 rounded" data-id="element-16253" />
                        </div>
                        <div className="ml-3 text-sm" data-id="element-16254">
                          <label htmlFor={`role-${role.id}`} className="font-medium text-gray-700" data-id="element-16255">
                            {role.name}
                          </label>
                          <p className="text-gray-500" data-id="element-16256">{role.description}</p>
                        </div>
                      </div>)}
                  </div>
                </div>
              </fieldset>
            </div>
          </div>
          {/* Email Preview */}
          {showPreview && <div className="mt-6 border border-gray-200 rounded-md overflow-hidden" data-id="element-16257">
              <div className="bg-gray-50 px-4 py-2 border-b border-gray-200" data-id="element-16258">
                <h3 className="text-sm font-medium text-gray-700" data-id="element-16259">
                  Email Preview
                </h3>
              </div>
              <div className="p-4 bg-white" data-id="element-16260">
                <div className="mb-2 text-sm font-medium text-gray-700" data-id="element-16261">
                  Subject: {formData.subject}
                </div>
                <div className="border border-gray-200 p-4 rounded-md" data-id="element-16262">
                  <div className="prose prose-sm max-w-none" dangerouslySetInnerHTML={{
                __html: formData.bodyHtml
              }} data-id="element-16263" />
                </div>
              </div>
            </div>}
          <div className="flex justify-end space-x-3" data-id="element-16264">
            <button type="button" onClick={onCancel} className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-16265">
              <XIcon className="h-4 w-4 mr-2" data-id="element-16266" />
              Cancel
            </button>
            <button type="submit" disabled={template?.isSystemLocked} className={`inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white ${template?.isSystemLocked ? 'bg-gray-400 cursor-not-allowed' : 'bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500'}`} data-id="element-16267">
              <SaveIcon className="h-4 w-4 mr-2" data-id="element-16268" />
              {template ? 'Update Template' : 'Create Template'}
            </button>
          </div>
        </form>
      </div>
    </div>;
}